<template>
  <div tabindex="0"
       :data-id="objectModelProperty.client_id"
       :data-type="objectModelProperty.property_type"
       class="form-element object-model-property"
       :class="propertyClasses"
       v-on:focus="replaceIdInRoute($event, true)"
       @click.stop="replaceIdInRoute">
    <div class="title-bar">
      <icon :class="ObjectModelPropertyDefinition.getIcon(objectModelProperty.property_type)">
        <span class="sr-only">Focused Element: {{ObjectModelPropertyDefinition.getDisplayName(objectModelProperty.property_type)}}</span>
      </icon>
      <strong>
        {{ObjectModelPropertyDefinition.getDisplayName(objectModelProperty.property_type)}}
        <debug-sort-order :sortable="objectModelProperty"/>
      </strong>
      <move-destroy :card="objectModelProperty" @remove:card="confirmDelete" class="suffix"></move-destroy>
    </div>
    <row>
      <column class="row-title" v-if="objectModelProperty.can(ObjectModelPropertyCapability.labelable)">
        <div :id="`scroll-to-label-${objectModelProperty.client_id}`"/>
        <object-model-property-labelable
          :object-model-property="objectModelProperty"
        ></object-model-property-labelable>
      </column>
    </row>

    <component
      :is="getComponentByType(objectModelProperty.property_type)"
      :object-model-property="objectModelProperty"
      @remove="remove"
    >
      <row>
        <column>
          <p class="small">
            <lengthable-display v-if="objectModelProperty.canHaveCharacterCount"
                                :model="objectModelProperty"></lengthable-display>
            <numeric-min-maxable-display v-if="objectModelProperty.hasMinOrMaxValue"
                                         :model="objectModelProperty"></numeric-min-maxable-display>
            <date-min-maxable-display v-if="objectModelProperty.hasDateMinOrMaxValue"
                                      :model="objectModelProperty"></date-min-maxable-display>
          </p>
        </column>
        <column class="align-right">
          <p class="small element-details">
            <formattable-display v-if="objectModelProperty.hasFormat"
                                 :model="objectModelProperty"></formattable-display>
            <indexable-display v-if="objectModelProperty.isIndexed" :model="objectModelProperty"></indexable-display>
            <template v-if="objectModelProperty.hasLinkedRelation">
              <linked-relationable-display :model="objectModelProperty"></linked-relationable-display>
              <linked-relation-propertyable-display
                v-if="objectModelProperty.hasLinkedRelation &&
                 objectModelProperty.can(ObjectModelPropertyCapability.linkToRelationPropertyable) &&
                 objectModelProperty.aggregate_type !== AggregateType.Count"
                :model="objectModelProperty"></linked-relation-propertyable-display>
            </template>
            <aggregatable-display
              v-if="objectModelProperty.canAggregate"
              :model="objectModelProperty"
              show-fallback
            ></aggregatable-display>

            <sensitive-data-icon v-if="propertyIsSecurityTagged" class="pl-1"></sensitive-data-icon>
          </p>
        </column>
      </row>
    </component>

    <modal :state="modalStateFor(vueModalID.confirmDeleteObjectModelProperty)"
           :index="vueModalID.confirmDeleteObjectModelProperty"
           @toggle-modal="hideModal(vueModalID.confirmDeleteObjectModelProperty)"
           v-if="modalStateFor(vueModalID.confirmDeleteObjectModelProperty).isVisible">
      <div slot="body">
        <h3 class="modal-title">Are You Sure?</h3>
        <p>Deleting {{objectModelProperty.labelTitleDisplay}}
          will make the property in ALL views and related items invalid. Are you sure you want to proceed?</p>
        <div class="cta">
          <button type="button" @click="hideModal(vueModalID.confirmDeleteObjectModelProperty)" class="btn block red outline">Cancel</button>
          <button type="button" @click="remove(objectModelProperty)" class="btn block green" data-action="affirm">Delete</button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import ObjectModelPropertyDefinition from "../../../common/models/objectModelProperties/objectModelPropertyDefinition";
  import propertyBaseMixin from "../mixins/property-base-mixin";
  import ShortText from "./property-short-text.vue"
  import LongText from "./property-long-text.vue"
  import PickList from "./property-pick-list.vue"
  import NumberProperty from "./property-number.vue"
  import DateTimeProperty from "./property-date-time.vue"
  import MappedProperty from "./property-mapped-property.vue"
  import RelationshipCount from "./property-relationship-count.vue"
  import AutoIncrement from "./property-auto-increment.vue"
  import DerivedField from "./property-derived-field.vue";
  import FileAttachment from './property-file-attachment.vue';
  import Aggregate from './property-aggregate.vue';
  import {Hash} from "../../../common/interfaces/hashInterfaces";
  import ObjectModelPropertyLabelable from "./object-model-property-labelable.vue";
  import {ObjectModelPropertyCapability} from "../../../common/models/objectModelProperties/objectModelPropertyCapability";
  import FormattableDisplay from "../../../common/components/modelProperties/formattable-display.vue"
  import LengthableDisplay from "../../../common/components/modelProperties/lengthable-display.vue"
  import IndexableDisplay from "../../../common/components/modelProperties/indexable-display.vue"
  import NumericMinMaxableDisplay from "../../../common/components/modelProperties/numeric-min-maxable-display.vue"
  import DateMinMaxableDisplay from "../../../common/components/modelProperties/date-min-maxable-display.vue"
  import LinkedRelationableDisplay from "../../../common/components/modelProperties/linked-relationable-display.vue"
  import LinkedRelationPropertyableDisplay from "../../../common/components/modelProperties/linked-relation-propertyable-display.vue"
  import AggregatableDisplay from "../../../common/components/modelProperties/aggregatable-display.vue"
  import {ObjectModelPropertyType} from "../../../common/enums/objectModelPropertyType";
  import MoveDestroy from "../../../common/components/moveDestroy.vue";
  import routerMixin from "../../../common/mixins/routerMixin";
  import ObjectModelProperty from "../../../common/models/objectModelProperties/objectModelProperty";
  import routerSelectedMixinFactory from "../../../common/mixins/routerSelectedMixinFactory";
  import confirmationModal from "../../../common/components/modals/confirmationModal.vue";
  import {AggregateType} from "~/enums/aggregateType";
  import DebugSortOrder from "~/components/debug/debug-sort-order.vue";
  import dataTaggableFieldMixin from "../mixins/dataTaggableFieldMixin";
  import {IObjectModelProperty} from "~/interfaces/objectModelInterfaces";

  export default Vue.extend({
    components: {
      "short-text": ShortText,
      "long-text": LongText,
      "pick-list": PickList,
      "number": NumberProperty,
      "date-time": DateTimeProperty,
      "mapped-property": MappedProperty,
      "relationship-count": RelationshipCount,
      "auto-increment": AutoIncrement,
      "derived-field": DerivedField,
      "file-attachment": FileAttachment,
      "aggregate": Aggregate,
      ObjectModelPropertyLabelable,
      FormattableDisplay,
      LengthableDisplay,
      IndexableDisplay,
      NumericMinMaxableDisplay,
      DateMinMaxableDisplay,
      LinkedRelationableDisplay,
      LinkedRelationPropertyableDisplay,
      AggregatableDisplay,
      MoveDestroy,
      confirmationModal,
      DebugSortOrder
    },
    mixins: [
      propertyBaseMixin,
      routerMixin,
      routerSelectedMixinFactory('objectModelProperty', '.object-model-property'),
      dataTaggableFieldMixin,
    ],
    data: function () {
      return {
        ObjectModelPropertyCapability: ObjectModelPropertyCapability,
        ObjectModelPropertyDefinition: ObjectModelPropertyDefinition,
        AggregateType
      };
    },
    computed: {
      propertyClasses(): Hash<boolean> {
        return {
          active: this.objectModelProperty.isSelected,
          [this.getComponentClass(this.objectModelProperty.property_type)]: true,
        };
      },
      dataTaggableField(): IObjectModelProperty {
        return this.objectModelProperty;
      },
    },
    methods: {
      confirmDelete() {
        this.showModal(this.vueModalID.confirmDeleteObjectModelProperty)
      },
      getComponentByType(type: ObjectModelPropertyType): string {
        return ObjectModelPropertyDefinition.getComponent(type);
      },
      getComponentClass(type: ObjectModelPropertyType): string {
        return ObjectModelPropertyDefinition.getComponentClass(type);
      },
      remove(property): void {
        property.destroy();
      },
      setSelected(objectModelProperty: ObjectModelProperty): void {
        objectModelProperty.select()
      }
    },
  });
</script>